import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { ComponentLike } from '@glint/template';

import type { Service, ValidationErrors } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import FormLeaveConfirmPopupComponent from 'webapp/components/form/leave-confirm/popup';
import NotificationsEditFormAlertComponent from 'webapp/components/notifications/edit/form/alert';
import NotificationsEditFormAnomalyComponent from 'webapp/components/notifications/edit/form/anomaly';
import type {
	Interface as BaseInterface,
	SaveFn,
	SendPreviewFn,
	ValidateFn,
} from 'webapp/components/notifications/edit/form/base';
import NotificationsEditFormBoardShareComponent from 'webapp/components/notifications/edit/form/board-share';
import NotificationsEditFormDigestComponent from 'webapp/components/notifications/edit/form/digest';
import NotificationsEditFormReportReminderComponent from 'webapp/components/notifications/edit/form/reminder';
import NotificationsEditFormReportScheduleSettingComponent from 'webapp/components/notifications/edit/form/report-schedule-setting';
import NotificationsEditFormScorecardComponent from 'webapp/components/notifications/edit/form/scorecard';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { Scorecard } from 'webapp/models-custom';
import type PopupModal from 'webapp/models-custom/popup/modal';

import type { ModelWithNotificationForPopup } from 'webapp/services/model-manager/notifications';

interface Args {
	popup: PopupModal;
	model: ModelWithNotificationForPopup;
	submitInProgress: boolean;
	origin: string;
	updateSubmitInProgress: (state: boolean) => void;
	close: () => void;
	forceClose: () => void;
	duplicate?: () => void; // if not given, model cannot be duplicated
	delete: () => void;
	onContentChange: () => void;
	afterSave?: () => void;
}

interface ComponentInterface {
	Args: Args;
}

export default class NotificationsEditFormComponent extends Component<ComponentInterface> {
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	// ----- Form / list components

	get componentClass(): ComponentLike<BaseInterface> | null {
		const model = this.args.model;
		switch (model.modelName) {
			case ModelName.SCORECARD:
				return (model as Scorecard).isDigest
					? NotificationsEditFormDigestComponent
					: NotificationsEditFormScorecardComponent;
			case ModelName.ALERT_SETTING:
				return NotificationsEditFormAlertComponent;
			case ModelName.METRIC_ANOMALY:
				return NotificationsEditFormAnomalyComponent;
			case ModelName.BOARD_SHARE:
				return NotificationsEditFormBoardShareComponent;
			case ModelName.REPORT_SCHEDULE_SETTING:
				return NotificationsEditFormReportScheduleSettingComponent;
			case ModelName.REMINDER:
				return NotificationsEditFormReportReminderComponent;
			default:
				return null;
		}
	}

	// ------ Validate START ------

	@tracked errors: ValidationErrors<string> = {};

	private validateFn: ValidateFn | null = null;

	get isValid() {
		return !Object.keys(this.errors).length;
	}

	validate() {
		const errors = this.validateFn?.() || {};
		this.errors = errors;
		return this.isValid;
	}

	@action
	private resetErrorData(key?: string): void {
		const errors = this.errors;

		if (!key) {
			next(() => {
				this.errors = {};
			});
		} else if (errors[key]) {
			delete errors[key];
			this.errors = errors;
		}
	}

	@action
	setValidateFn(fn: ValidateFn) {
		this.validateFn = fn;
	}

	// ------ Validate END ------

	// ------ Save START ------

	private saveFn: SaveFn | null = null;

	@action
	save() {
		if (this.args.submitInProgress) {
			return;
		}

		if (!this.validate()) {
			return;
		}

		const afterSave = this.args.afterSave;

		this.args.updateSubmitInProgress(true);

		const isNew = this.args.model.isNew;

		const layer = this.infolayerService.loading(
			`${isNew ? 'Creating' : 'Updating'} your notification...`,
		);

		this.saveFn?.()
			.then(
				() => {
					if (afterSave) {
						afterSave();
					}
					layer.ok(`Notification ${isNew ? 'created' : 'updated'}!`);
					// force-close so a save that leaves the model dirty (e.g. saving a
					// copy of a not-owned scorecard) is not blocked by the leave-confirm
					this.args.forceClose();
				},
				(errorData: unknown) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.finally(() => {
				this.args.updateSubmitInProgress(false);
			});
	}

	@action
	setSaveFn(fn: SaveFn) {
		this.saveFn = fn;
	}

	// ------ Save END ------

	// ------ Send preview START ------

	private sendPreviewFn: SendPreviewFn | null = null;

	@action
	setSendPreviewFn(fn: SendPreviewFn) {
		this.sendPreviewFn = fn;
	}

	@action
	sendPreview() {
		this.sendPreviewFn?.();
	}

	get isScorecard() {
		const model = this.args.model;

		return model.modelName === ModelName.SCORECARD && !(model as Scorecard).isDigest;
	}

	get showSendPreview() {
		return this.isScorecard && this.args.model.isNew;
	}

	// ------ Send preview END ------

	@action
	checkIsDirty(): boolean {
		return this.args.model.isDirty;
	}

	get showDelete() {
		return !this.args.model.isNew;
	}

	get hasTitle() {
		return this.isScorecard;
	}

	get showDuplicate() {
		return !this.args.model.isNew && !!this.args.duplicate;
	}

	@action
	duplicate() {
		this.args.duplicate?.();
	}

	get saveButtonText() {
		return this.args.model.isNew ? 'Create' : 'Save';
	}

	<template>
		<div class="content2 content2--shown">
			{{#if this.componentClass}}
				{{#let (component this.componentClass) as |ComponentName|}}
					<ComponentName
						@model={{@model}}
						@errors={{this.errors}}
						@resetErrorData={{this.resetErrorData}}
						@submitInProgress={{@submitInProgress}}
						as |exports|
					>
						<div class="form">

							<div class="form__top">
								<div class="form__setup">

									<exports.main
										@origin={{@origin}}
										@setValidateFn={{this.setValidateFn}}
										@setSaveFn={{this.setSaveFn}}
										@setSendPreviewFn={{this.setSendPreviewFn}}
										@onContentChange={{@onContentChange}}
									/>
								</div>
							</div>

							<div class="form__bottom">
								<div class="controls2">
									<div class="controls2__left">
										{{#if this.hasTitle}}
											<div class="name">
												<exports.title />
											</div>
										{{else if this.showDelete}}
											<SubmitButtonComponent
												@text="Delete"
												@extraClass="delete"
												@theme="red-outlined"
												@onTrigger={{@delete}}
											/>
										{{/if}}
									</div>
									<div class="controls2__right">
										<SubmitButtonComponent
											@text="Cancel"
											@extraClass="cancel"
											@theme="grey-empty"
											@onTrigger={{@close}}
										/>

										{{#if this.showSendPreview}}
											<SubmitButtonComponent
												@text="Send a preview"
												@extraClass="send-preview"
												@theme="blue-outlined"
												@onTrigger={{this.sendPreview}}
											/>
										{{/if}}

										{{#if this.hasTitle}}
											{{#if this.showDelete}}
												<SubmitButtonComponent
													@text="Delete"
													@extraClass="delete"
													@theme="red-outlined"
													@onTrigger={{@delete}}
												/>
											{{/if}}
										{{/if}}

										{{#if this.showDuplicate}}
											<SubmitButtonComponent
												@text="Duplicate"
												@extraClass="duplicate"
												@theme="blue-outlined"
												@onTrigger={{this.duplicate}}
											/>
										{{/if}}

										<FormLeaveConfirmPopupComponent
											@popup={{@popup}}
											@save={{this.save}}
											@checkIsDirty={{this.checkIsDirty}}
											as |controls|
										>
											<controls.save
												@text={{this.saveButtonText}}
												@extraClass="save"
												@submitInProgress={{@submitInProgress}}
											/>
										</FormLeaveConfirmPopupComponent>
									</div>
								</div>
							</div>

						</div>
					</ComponentName>
				{{/let}}
			{{/if}}
		</div>
	</template>
}
